/**
 * Application Bootstrap
 *
 * This file is the true entrypoint of the application.
 * It loads config.json and initializes error tracking (Sentry) BEFORE
 * any React code is loaded, ensuring all errors are captured from the start.
 *
 * NOTE: We use @sentry/react here for consistency with the rest of the app
 * and to ensure ErrorBoundary components use the same initialized Sentry instance.
 */

// Third-party packages
import * as Sentry from '@sentry/react';

// Internal libraries (@stonal-tech/*)
import {
	ALL_DENY_URLS,
	ALL_IGNORED_ERRORS,
	IGNORED_TRANSACTIONS,
	SENTRY_DISABLED_ENVIRONMENTS,
	type SentryDisabledEnvironment,
} from '@stonal-tech/lib-front-utils-ts';

// Project modules
import type {
	TypeConfig,
} from '@modules/config';

interface BootstrapOptions {
	/** Path prefix for config URL: 'webapp' (LPdI Pro) or 'de' (Mes Offres) */
	pathPrefix: 'webapp' | 'de';
}

/** Config shape as returned by config.json (may include nested SENTRY_CONF) */
interface FetchedConfig extends Record<string, unknown> {
	SENTRY_DSN_JS?: string;
	SENTRY_CONF?: {
		DSN?: string;
		ENVIRONMENT?: string;
	};
	STONAL_ENV?: string;
}

const getConfigUrl = (pathPrefix: string): string => {
	const currentDomain = window.location.hostname;
	const currentPort = window.location.port;
	const isLocalEnv = currentDomain.includes('localhost') || currentPort === '5173';
	return isLocalEnv
		? `/${pathPrefix}/.well-known/config.json?t=${Date.now()}`
		: `/app/${pathPrefix}/.well-known/config.json?t=${Date.now()}`;
};

const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0';

/**
 * Loads config, initializes Sentry (only when not in disabled envs), then runs the app loader.
 * Call this from the entry point (e.g. app.lpdipro.js) and pass loadApp to run the React app.
 */
export const runBootstrap = async (
	options: BootstrapOptions,
	loadApp: () => Promise<unknown>,
): Promise<void> => {
	const {
		pathPrefix,
	} = options;
	const configUrl = getConfigUrl(pathPrefix);
	const appName = pathPrefix === 'de' ? 'mesoffres' : 'lpdipro';

	try {
		const response = await fetch(configUrl);
		if (!response.ok) {
			await loadApp();
			return;
		}

		const config = (await response.json()) as FetchedConfig;
		window.STONAL_CONFIG = config as unknown as TypeConfig;

		const dsn = config.SENTRY_DSN_JS || config.SENTRY_CONF?.DSN;
		const environment = config.SENTRY_CONF?.ENVIRONMENT ?? config.STONAL_ENV ?? 'development';
		const isFilteredEnv = SENTRY_DISABLED_ENVIRONMENTS.includes(environment as SentryDisabledEnvironment);

		if (dsn) {
			Sentry.init({
				attachStacktrace: true,
				denyUrls: [
					...ALL_DENY_URLS,
				],
				dsn,

				// Disable in local/dev environments
				enabled: !isFilteredEnv,

				environment,
				ignoreErrors: [
					...ALL_IGNORED_ERRORS,
				],
				ignoreTransactions: [
					...IGNORED_TRANSACTIONS,
				],
				integrations: [
					Sentry.replayIntegration(),
				],
				maxBreadcrumbs: 50,
				normalizeDepth: 5,
				release: `${appName}@${appVersion}`,
				replaysOnErrorSampleRate: 1.0,
				replaysSessionSampleRate: 0,
				sampleRate: 1.0,
				tracesSampleRate: 0.1,
			});
			console.info(`[ErrorTracking] Sentry initialized for '${appName}'`);
		}
	} catch (error) {
		console.warn('[ErrorTracking] Failed to pre-load config:', error);
	}

	await loadApp();
};
